import { useMemo } from 'react';

import { parseCalc } from './utils';

import styles from './styles.module.scss';

export type Cutout = ({ top: string; bottom?: never } | { top?: never; bottom: string }) &
  ({ left: string; right?: never } | { right: string; left?: never }) & {
    width: string;
    height: string;
  };

type ProcessedCutout = Cutout & { index: number };

type CutoutBackdropProps = {
  cutouts?: Cutout[];
  testId: string;
};

type Step<Direction extends 'v' | 'h'> = (Direction extends 'v'
  ? { anchor: 'top' | 'bottom' }
  : { anchor: 'left' | 'right' }) & {
  position: number;
  type: 'start' | 'end';
  cutouts: ProcessedCutout[];
};

type VerticalRange = {
  start: number;
  end: number;
  collidedElements: ProcessedCutout[];
  anchor: 'top' | 'bottom';
};
type HorizontalRange = {
  start: number;
  end: number;
  anchor: 'left' | 'right';
};
type ProcessedRange = VerticalRange & {
  horizontalRanges: HorizontalRange[];
};

export const CutoutBackdrop = ({ cutouts, testId }: CutoutBackdropProps) => {
  const { ranges, windowWidth, windowHeight } = useMemo(() => {
    if (!cutouts?.length) {
      return { steps: [], ranges: [], windowWidth: 0, windowHeight: 0 };
    }

    const windowWidth = window.innerWidth ?? document.documentElement.clientWidth;
    const windowHeight = window.innerHeight ?? document.documentElement.clientHeight;

    const verticalSteps: Record<string, Step<'v'>> = {};

    cutouts.forEach((cutout, index) => {
      const height = parseCalc(cutout.height, windowHeight);

      if (cutout.top) {
        //TOP + HEIGHT
        const top = parseCalc(cutout.top, windowHeight);

        if (verticalSteps[`${top}-start`]) {
          verticalSteps[`${top}-start`].cutouts.push({ ...cutout, index });
        } else {
          verticalSteps[`${top}-start`] = {
            position: top,
            type: 'start',
            cutouts: [{ ...cutout, index }],
            anchor: 'top',
          };
        }

        if (verticalSteps[`${top + height}-end`]) {
          verticalSteps[`${top + height}-end`].cutouts.push({ ...cutout, index });
        } else {
          verticalSteps[`${top + height}-end`] = {
            position: top + height,
            type: 'end',
            cutouts: [{ ...cutout, index }],
            anchor: 'top',
          };
        }
      } else {
        //BOTTOM + HEIGHT
        const bottom = parseCalc(cutout.bottom, windowHeight);

        if (verticalSteps[`${windowHeight - bottom - height}-start`]) {
          verticalSteps[`${windowHeight - bottom - height}-start`].cutouts.push({
            ...cutout,
            index,
          });
        } else {
          verticalSteps[`${windowHeight - bottom - height}-start`] = {
            position: windowHeight - bottom - height,
            type: 'start',
            cutouts: [{ ...cutout, index }],
            anchor: 'bottom',
          };
        }

        if (verticalSteps[`${windowHeight - bottom}-end`]) {
          verticalSteps[`${windowHeight - bottom}-end`].cutouts.push({ ...cutout, index });
        } else {
          verticalSteps[`${windowHeight - bottom}-end`] = {
            position: windowHeight - bottom,
            type: 'end',
            cutouts: [{ ...cutout, index }],
            anchor: 'bottom',
          };
        }
      }
    });

    const getCollidedElements = (
      verticalRanges: VerticalRange[],
      step: Step<'v'>,
      stepArray: Step<'v'>[],
      index: number,
    ): ProcessedCutout[] => {
      if (!verticalRanges.length) {
        return [...step.cutouts];
      }

      if (step.type === 'end') {
        return verticalRanges[verticalRanges.length - 1].collidedElements.filter(
          (element) => !step.cutouts.find((cutout) => cutout.index === element.index),
        );
      } else {
        const previousStep = index - 1 >= 0 ? stepArray[index - 1] : undefined;
        if (!previousStep) {
          return [...step.cutouts];
        }

        switch (previousStep.type) {
          case 'start':
            return [...previousStep.cutouts, ...step.cutouts];
          case 'end':
            return [
              ...previousStep.cutouts.filter(
                (element) => !step.cutouts.find((cutout) => cutout.index !== element.index),
              ),
              ...step.cutouts,
            ];
        }
      }
    };

    const stepArray = Object.values(verticalSteps).sort((a, b) => {
      if (a.position === b.position) {
        if (a.type === b.type) {
          return 1;
        }

        return a.type === 'start' ? 1 : -1;
      }

      return a.position > b.position ? 1 : -1;
    });
    const fillingVerticalRanges: VerticalRange[] = [];
    stepArray.forEach((step, index) => {
      const nextStep = index + 1 < stepArray.length ? stepArray[index + 1] : null;
      if (nextStep) {
        fillingVerticalRanges.push({
          start: step.position,
          end: nextStep.position,
          collidedElements: getCollidedElements(fillingVerticalRanges, step, stepArray, index),
          anchor: step.anchor,
        });
      }
    });

    //Clean up unecessary ranges where start = end
    const verticalRanges = fillingVerticalRanges.filter((range) => range.start !== range.end);

    const processedRanges: ProcessedRange[] = [];
    verticalRanges.forEach((range) => {
      const horizontalSteps: Record<string, Omit<Step<'h'>, 'cutouts'>> = {};

      range.collidedElements.forEach((cutout) => {
        const width = parseCalc(cutout.width, windowWidth);

        if (cutout.left) {
          //LEFT + WIDTH
          const left = parseCalc(cutout.left, windowWidth);

          horizontalSteps[`${left}-start`] = {
            position: left,
            type: 'start',
            anchor: 'left',
          };

          horizontalSteps[`${left + width}-end`] = {
            position: left + width,
            type: 'end',
            anchor: 'left',
          };
        } else {
          //RIGHT + WIDTH
          const right = parseCalc(cutout.right, windowWidth);

          horizontalSteps[`${windowWidth - width - right}-start`] = {
            position: windowWidth - width - right,
            type: 'start',
            anchor: 'right',
          };

          horizontalSteps[`${windowWidth - right}-end`] = {
            position: windowWidth - right,
            type: 'end',
            anchor: 'right',
          };
        }
      });

      const horizontalRanges: HorizontalRange[] = [];
      const stepArray = Object.values(horizontalSteps).sort((a, b) => {
        if (a.position === b.position) {
          if (a.type === b.type) {
            return 1;
          }

          return a.type === 'start' ? 1 : -1;
        }

        return a.position > b.position ? 1 : -1;
      });
      const cleanStepArray = stepArray
        .slice(0, stepArray.length - 1)
        .filter((step, index) => step.type !== stepArray[index + 1].type);
      cleanStepArray.push(stepArray[stepArray.length - 1]);

      cleanStepArray.slice(0, cleanStepArray.length - 1).forEach((step, index) => {
        if (step.type === 'start') {
          horizontalRanges.push({
            start: step.position,
            end: cleanStepArray[index + 1].position,
            anchor: step.anchor,
          });
        }
      });
      processedRanges.push({ ...range, horizontalRanges });
    });

    return {
      ranges: processedRanges,
      windowWidth,
      windowHeight,
    };
  }, [cutouts]);

  if (!cutouts?.length) {
    return (
      <div
        className={styles.backdrop}
        style={{ opacity: '0.5', width: '100%', height: '100%', left: 0, top: 0 }}
      />
    );
  }

  const renderRanges = () => {
    return ranges.map((range, vIndex) => {
      if (!range.horizontalRanges.length) {
        return (
          <div
            className={styles.backdrop}
            style={{
              opacity: '0.5',
              width: '100%',
              height: `${range.end - range.start}px`,
              top: range.start,
              left: 0,
            }}
            key={`range-full-${vIndex}`}
            data-testid={`onboarding-${testId}-cutout-range-full-${vIndex}`}
          />
        );
      }

      const firstRange = range.horizontalRanges[0];
      const lastRange = range.horizontalRanges[range.horizontalRanges.length - 1];
      return (
        <>
          {firstRange.start > 0 && (
            <div
              className={styles.backdrop}
              style={{
                opacity: '0.5',
                left: 0,
                right:
                  firstRange.anchor === 'right' ? `${windowWidth - firstRange.start}px` : undefined,
                width: firstRange.anchor === 'left' ? `${firstRange.start}px` : undefined,
                top: `${range.start}px`,
                height: `${range.end - range.start}px`,
              }}
              key={`range-${vIndex}-start`}
              data-testid={`onboarding-${testId}-cutout-range-${vIndex}-start`}
            />
          )}
          {range.horizontalRanges
            .slice(0, range.horizontalRanges.length - 1)
            .map((horizontalRange, hIndex) =>
              horizontalRange.end !== range.horizontalRanges[hIndex + 1].start ? (
                <div
                  className={styles.backdrop}
                  style={{
                    opacity: '0.5',
                    left:
                      horizontalRange.anchor === 'left' ? `${horizontalRange.end}px` : undefined,
                    right:
                      horizontalRange.anchor === 'right'
                        ? `${range.horizontalRanges[hIndex + 1].start}px`
                        : undefined,
                    width: `${range.horizontalRanges[hIndex + 1].start - horizontalRange.end}px`,
                    top: `${range.start}px`,
                    height: `${range.end - range.start}px`,
                  }}
                  key={`range-${vIndex}-middle-${hIndex}`}
                  data-testid={`onboarding-${testId}-cutout-range-${vIndex}-middle-${hIndex}`}
                />
              ) : null,
            )}

          {lastRange.end < windowWidth && (
            <div
              className={styles.backdrop}
              style={{
                opacity: '0.5',
                left: lastRange.anchor === 'left' ? `${lastRange.end}px` : undefined,
                right: 0,
                width: `${windowWidth - lastRange.end}px`,
                top: range.start,
                height: `${range.end - range.start}px`,
              }}
              key={`range-${vIndex}-end`}
              data-testid={`onboarding-${testId}-cutout-range-${vIndex}-end`}
            />
          )}
        </>
      );
    });
  };

  return (
    <>
      {ranges[0].start > 0 && (
        <div
          className={styles.backdrop}
          style={{
            opacity: '0.5',
            width: '100%',
            height: `${ranges[0].start}px`,
            top: 0,
            left: 0,
          }}
          key="start"
          data-testid={`onboarding-${testId}-cutout-start`}
        />
      )}
      {renderRanges()}
      {ranges[ranges.length - 1].end < windowHeight && (
        <div
          className={styles.backdrop}
          style={{
            opacity: '0.5',
            width: '100%',
            top: `${ranges[ranges.length - 1].end}px`,
            height: '100%',
            left: 0,
          }}
          key="end"
          data-testid={`onboarding-${testId}-cutout-end`}
        />
      )}
    </>
  );
};
